/**
 * Text Build-Up — Aussage, die sich Zeile für Zeile aufbaut.
 *
 * Erklär-/Statement-Szene: mehrere Phrasen stapeln sich nacheinander zu einer
 * großen Aussage. Jede Zeile steigt mit klärendem Blur ein; mit Sternchen
 * markierte Wörter (`*Wort*`) poppen in der Akzentfarbe. Perfekt, um einen
 * Gedanken Schritt für Schritt zu entwickeln statt alles auf einmal zu zeigen.
 *
 * Items-Format:  eine Phrase pro Zeile, Keyword optional mit *Sternchen*.
 *   z. B. ["Dein Lieblings-Café.", "Der Laden um die Ecke.", "Jetzt *alle* auf Allyver."]
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, gsapSetLine,
  makeTl, typeSize, weights, withAlpha, colors as themeColors,
} from "../../reel/sceneShared.js";

/** Escaped HTML, mit `*Keyword*` → Akzent-Span. */
function renderPhrase(raw: string, accent: string): string {
  return raw
    .split(/(\*[^*]+\*)/g)
    .filter((s) => s.length > 0)
    .map((part) => {
      if (part.startsWith("*") && part.endsWith("*") && part.length > 2) {
        const inner = part.slice(1, -1);
        return `<span style="color:${accent};text-shadow:0 0 38px ${withAlpha(accent, 0.5)};">${escapeHtml(inner)}</span>`;
      }
      return escapeHtml(part);
    })
    .join("");
}

export const textBuildUp: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const isVertical = ctx.format === "9:16";

  const eyebrow = inputs.extras?.eyebrow != null ? String(inputs.extras.eyebrow) : "";

  const lines = (inputs.items?.length
    ? inputs.items
    : [
        "Dein Lieblings-Café.",
        "Der Shop um die Ecke.",
        "Die Werkstatt deines Vertrauens.",
        "Jetzt *alle* an einem Ort.",
      ]
  ).slice(0, isVertical ? 5 : 4);

  const sm = typeSize("sm", ctx.format);
  const linePx = isVertical ? typeSize("lg", ctx.format) * 0.94 : typeSize("lg", ctx.format) * 0.92;

  const wrapId = `${clipPrefix}-wrap`;
  const eyeId = `${clipPrefix}-eye`;
  const lineIds = lines.map((_, i) => `${clipPrefix}-ln-${i}`);

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "spotlight", accent });

  const eyePx = sm * 0.62;
  const linesHtml = lines
    .map((raw, i) => `<div id="${lineIds[i]}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${linePx}px;line-height:1.08;letter-spacing:-2px;color:${themeColors.fg};text-wrap:balance;will-change:transform,opacity,filter;">${renderPhrase(raw, accent)}</div>`)
    .join("");

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:flex-start;justify-content:center;gap:${isVertical ? 16 : 14}px;padding:${isVertical ? "130px 96px" : "120px 160px"};overflow:hidden;">
    ${eyebrow ? `<div id="${eyeId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${eyePx}px;letter-spacing:6px;text-transform:uppercase;color:${accent};display:inline-flex;align-items:center;gap:14px;margin-bottom:${isVertical ? 12 : 10}px;"><span style="width:34px;height:2px;background:${accent};"></span><span>${escapeHtml(eyebrow)}</span></div>` : ""}
    ${linesHtml}
  </div>`;

  const init = [
    eyebrow ? gsapSetLine(`#${eyeId}`, { opacity: 0, y: -8 }) : "",
    ...lineIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, y: 30, filter: "blur(10px)" })),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [];
  if (eyebrow) tweens.push(t.to(`#${eyeId}`, { opacity: 1, y: 0, duration: 0.4, ease: "power2.out" }, 0.1));

  const start = eyebrow ? 0.4 : 0.25;
  // Letzte Zeile bekommt etwas mehr Luft (Pointe).
  const revealWindow = Math.min(clipDurationSec - 0.8, Math.max(1.2, clipDurationSec * 0.62));
  const step = revealWindow / Math.max(lines.length, 1);

  lines.forEach((_, i) => {
    const isLast = i === lines.length - 1;
    const at = start + i * step;
    tweens.push(
      t.to(
        `#${lineIds[i]}`,
        { opacity: 1, y: 0, filter: "blur(0px)", duration: isLast ? 0.6 : 0.5, ease: isLast ? "back.out(1.5)" : "power3.out" },
        at,
      ),
    );
    if (isLast) {
      tweens.push(t.to(`#${lineIds[i]}`, { scale: 1.04, duration: 0.5, ease: "power2.inOut", transformOrigin: "0% 50%" }, at + 0.1));
    }
  });

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
